'use client';

import React, { createContext, useContext, useCallback, useState, useTransition, TransitionStartFunction } from 'react';

// Breadcrumb types
type BreadcrumbAction = {
  icon: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  disabled?: boolean;
  ariaLabel?: string;
};

type BreadcrumbItem = {
  label: string;
  href?: string;
  isCurrentPage?: boolean;
  action?: BreadcrumbAction;
};

// Filter types (extensible for different filter types)
type FilterState = {
  search?: string;
  tags?: string[];
  [key: string]: string | string[] | undefined;
};

type FilterSelectOption = {
  label: string;
  value: string;
};

type FilterOptions = {
  search?: boolean;
  tags?: FilterSelectOption[];
  [key: string]: boolean | FilterSelectOption[] | undefined;
};

type AppContextType = {
  // Breadcrumb state
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (items: BreadcrumbItem[]) => void;
  addBreadcrumb: (item: BreadcrumbItem) => void;
  clearBreadcrumbs: () => void;

  // Filter state
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  updateFilter: (key: keyof FilterState, value: any) => void;
  clearFilters: () => void;

  // Filter options
  filterOptions: FilterOptions;
  setFilterOptions: (options: FilterOptions) => void;
  hasFilterOptions: boolean;

  // Loading state
  loading: boolean;
  startTransition: TransitionStartFunction;
};

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [breadcrumbs, setBreadcrumbsState] = useState<BreadcrumbItem[]>([]);
  const [filters, setFiltersState] = useState<FilterState>({});
  const [filterOptions, setFilterOptionsState] = useState<FilterOptions>({});
  const [loading, startTransition] = useTransition();

  // Breadcrumb methods
  const setBreadcrumbs = useCallback((items: BreadcrumbItem[]) => {
    setBreadcrumbsState(items);
  }, []);

  const addBreadcrumb = useCallback((item: BreadcrumbItem) => {
    setBreadcrumbsState((prev) => [...prev, item]);
  }, []);

  const clearBreadcrumbs = useCallback(() => {
    setBreadcrumbsState([]);
  }, []);

  // Filter methods
  const setFilters = useCallback((newFilters: FilterState) => {
    setFiltersState(newFilters);
  }, []);

  const updateFilter = useCallback((key: keyof FilterState, value: any) => {
    setFiltersState((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState({});
  }, []);

  // Filter options methods
  const setFilterOptions = useCallback((options: FilterOptions) => {
    setFilterOptionsState(options);
  }, []);

  return (
    <AppContext.Provider
      value={{
        // Breadcrumbs
        breadcrumbs,
        setBreadcrumbs,
        addBreadcrumb,
        clearBreadcrumbs,

        // Filters
        filters,
        setFilters,
        updateFilter,
        clearFilters,

        // Filter options
        filterOptions,
        setFilterOptions,
        hasFilterOptions: Object.keys(filterOptions).length > 0,

        // Loading
        loading,
        startTransition,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

// Convenience hooks for specific functionality
export function useBreadcrumb() {
  const { breadcrumbs, setBreadcrumbs, addBreadcrumb, clearBreadcrumbs } = useApp();
  return {
    breadcrumbs,
    setBreadcrumbs,
    addBreadcrumb,
    clearBreadcrumbs,
  };
}

export function useFilters() {
  const { filters, setFilters, updateFilter, clearFilters, filterOptions, setFilterOptions, hasFilterOptions } = useApp();
  return {
    filters,
    setFilters,
    updateFilter,
    clearFilters,
    filterOptions,
    setFilterOptions,
    hasFilterOptions,
  };
}
